import DndItem, { DndItemProps } from './DndItem'
import { createContext, useEffect, useState } from 'react'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

function getInstanceId() {
  return Symbol('grid')
}

const InstanceIdContext = createContext<symbol | null>(null)

export type DndIdentifiable = { id: string | number }

export interface DndGridProps<T extends DndIdentifiable> {
  items: T[]
  onItemsChange: (items: T[]) => void
  renderItem: DndItemProps<T>['renderItem']
  styles?: {
    idle?: React.CSSProperties
    dragging?: React.CSSProperties
    over?: React.CSSProperties
  }
}

export const DndGrid = <T extends DndIdentifiable>({
  items,
  onItemsChange,
  renderItem,
  styles = {},
}: DndGridProps<T>) => {
  const [instanceId] = useState(getInstanceId)
  const [localItems, setLocalItems] = useState(items)

  useEffect(() => {
    setLocalItems(items)
  }, [items])
  useEffect(() => {
    return monitorForElements({
      canMonitor({ source }) {
        return source.data.instanceId === instanceId
      },
      onDrop({ source, location }) {
        const destination = location.current.dropTargets[0]
        if (!destination) {
          return
        }

        const startId = source.data.id
        const destinationId = destination.data.id

        const startIndex = localItems.findIndex((i) => i.id === startId)
        const destinationIndex = localItems.findIndex(
          (i) => i.id === destinationId
        )

        if (startIndex === -1 || destinationIndex === -1) {
          return
        }

        const updatedItems = [...localItems]
        const [movedItem] = updatedItems.splice(startIndex, 1)
        updatedItems.splice(destinationIndex, 0, movedItem)

        setLocalItems(updatedItems)
        onItemsChange(updatedItems)
      },
    })
  }, [instanceId, localItems, onItemsChange])

  return (
    <InstanceIdContext.Provider value={instanceId}>
      {localItems.map((item, index) => (
        <DndItem<T>
          key={index}
          item={item}
          styles={styles}
          context={InstanceIdContext}
          renderItem={renderItem}
        />
      ))}
    </InstanceIdContext.Provider>
  )
}
